import { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { sdkWrapperURL } from "./api-url-list";
import LoadingScreen from "../components/LoadingScreen";

const HARDCODED_TOKEN = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJleHAiOjE4NTcyOTg2MDksInVzZXJfaWQiOiIwZDRiMjk0Mi0wYWI1LTQzYzEtYmRmMy03MzU0MjQzNTE5Y2QiLCJ1c2VybmFtZSI6Ijk4MzYzNzQyOTIiLCJ1c2VyX3R5cGUiOiIzIiwiY2xpZW50X2lkIjoiYjI4YTg1YmEtZGNkMi00NmJmLTkxNDQtNzU1OTNmMGY3NThlIiwibGFzdF9sb2dpbiI6IjIwMjYtMDItMTJUMTI6MTY6NDkuMzYyODU1KzAwOjAwIiwibG9naW5fdHlwZSI6ImF1dGgiLCJmaXJzdF9uYW1lIjoiIiwibGFzdF9uYW1lIjoiIiwiY291bnRyeV9pZCI6IjEwMSIsImRldmljZV9pZCI6Ik5vbmUiLCJhY2NvdW50X2V4cGlyeV9kYXRlIjoiMjAzNC0xMi0zMVQwMDowMDowMCswMDowMCIsInBob25lY29kZSI6IjkxIn0.fIgDCoH06TCKetObgNUSf1fJg09V1y0YeScIZOw_3MU";

const AuthContext = createContext({
  getToken: async () => "",
  userData: { employee_id: "", referral_code: "" },
  agentInfo: null,
  referralValidated: false,
  setReferralValidated: () => {},
  setAgentInfo: () => {},
  logout: () => {},
  logoutAgent: () => {},
});

const AuthProvider = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(true);
  const [userData, setUserData] = useState({ employee_id: "", referral_code: "" });
  const [referralValidated, setReferralValidated] = useState(() => {
    return sessionStorage.getItem("referral_validated") === "true";
  });
  const [agentInfo, setAgentInfoState] = useState(() => {
    try {
      const stored = sessionStorage.getItem("agent_info");
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const updateReferralValidated = (value) => {
    setReferralValidated(value);
    if (value) sessionStorage.setItem("referral_validated", "true");
    else sessionStorage.removeItem("referral_validated");
  };

  const updateAgentInfo = (info) => {
    setAgentInfoState(info);
    if (info) sessionStorage.setItem("agent_info", JSON.stringify(info));
    else sessionStorage.removeItem("agent_info");
  };

  const logout = () => {
    sessionStorage.removeItem("referral_validated");
    sessionStorage.removeItem("agent_info");
    setReferralValidated(false);
    setAgentInfoState(null);
    navigate("/get-started", { replace: true });
  };

  const logoutAgent = () => {
    sessionStorage.removeItem("referral_validated");
    sessionStorage.removeItem("agent_info");
    setReferralValidated(false);
    setAgentInfoState(null);
    window.location.href = "/get-started";
  };

  const getUserData = async (token = "") => {
    try {
      if (token.length <= 0) throw new Error("Invalid Token");
      const profileResp = await fetch(sdkWrapperURL("/users/profile/view"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      const profileRespJSON = await profileResp.json();
      if (profileRespJSON?.statusCode?.toString().startsWith("2"))
        return {
          employee_id: profileRespJSON?.profile?.employee_id || "",
          referral_code: profileRespJSON?.profile?.referral_details?.[0]?.code || "",
        };
      else throw new Error(profileRespJSON?.message ?? "Error in Fetching Profile Data");
    } catch (err) {
      console.error(err);
    }
  };

  const publicPaths = ["/get-started", "/validate-referral"];

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const ud = await getUserData(HARDCODED_TOKEN);
        setUserData(ud || { employee_id: "", referral_code: "" });

        const isValidated = sessionStorage.getItem("referral_validated") === "true";
        if (isValidated) {
          if (publicPaths.includes(location.pathname)) navigate("/", { replace: true });
        } else {
          if (!publicPaths.includes(location.pathname)) navigate("/get-started", { replace: true });
        }
      } catch (err) {
        console.error(err);
        navigate("/get-started", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getToken = () =>
    new Promise((resolve) => {
      resolve(HARDCODED_TOKEN);
    });

  return isLoading ? (
    <LoadingScreen />
  ) : (
    <AuthContext.Provider value={{ getToken, userData, agentInfo, referralValidated, setReferralValidated: updateReferralValidated, setAgentInfo: updateAgentInfo, logout, logoutAgent }}>
      {props?.children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
